// menschenZusatzSpalten.tsx — Spalten der Liste „Mensch im Unternehmen" wie in
// der früheren Kontaktakte (redesign/MenschenImUnternehmenSection.tsx, User
// 28.09.2026): Telefon, E-Mail, Abteilung, bAV, bKV, UV, Betriebshaftpflicht.
//
// Abteilung und die vier Absicherungen hängen an (Firma, Person) und werden
// sofort gespeichert (`company-employee-coverage/:firmaId`) — deshalb nur, wenn
// die Firma schon eine Id hat (Akte) und die Person gespeichert ist. In der
// gesperrten Akte sperrt das umgebende `<fieldset disabled>` die Felder mit.
//
// Telefon und E-Mail sind seit 30.09.2026 nur die GESCHÄFTLICHEN Wege der
// Person mit Firmenbezug zu dieser Firma (contactToRelationItems). Der Stift
// schaltet eine Zeile auf Inline-Editing (`InlineBearbeitung`): beide Felder
// werden Eingaben und beim Verlassen sofort gespeichert.

import * as React from "react";
import type { BeziehungsZeile, ZusatzSpalte } from "../forms/BeziehungsTabelle";
import {
  listEmployeeCoverage,
  saveEmployeeCoverage,
  type EmployeeCoverageRow,
} from "../../services/companyEmployeeCoverageService";
import type { MenschEntry } from "./MenschenFuersUnternehmenList";
import { formatiereTelefon, pruefeTelefon } from "@kmvs/forms-core";
import { useTelefonFeld } from "../forms/widgets/useTelefonFeld";
import type { FirmenKommunikationTyp } from "../../services/firmenKommunikationService";

export type AbsicherungsFeld = "bav" | "bkv" | "uv" | "betriebshaftpflicht";

export type MitarbeiterAbsicherung = {
  /** Firma gespeichert und Daten geladen → Spalten zeigen. */
  aktiv: boolean;
  fehler: boolean;
  zeile: (personId: number) => EmployeeCoverageRow;
  /** Speichert sofort; das Versprechen scheitert, wenn der Server ablehnt. */
  speichern: (row: EmployeeCoverageRow) => Promise<void>;
};

const LEER = (personContactId: number): EmployeeCoverageRow => ({
  personContactId,
  department: null,
  bav: false,
  bkv: false,
  uv: false,
  betriebshaftpflicht: false,
});

/** Lädt die Absicherungen der Firma, sobald die Liste offen ist. */
export function useMitarbeiterAbsicherung(firmaId: number | undefined, laden: boolean): MitarbeiterAbsicherung {
  const [daten, setDaten] = React.useState<Map<number, EmployeeCoverageRow> | null>(null);
  const [fehler, setFehler] = React.useState(false);

  React.useEffect(() => {
    if (!firmaId || !laden || daten !== null || fehler) return;
    let aus = false;
    listEmployeeCoverage(firmaId)
      .then((rows) => !aus && setDaten(new Map(rows.map((r) => [r.personContactId, r]))))
      // Ladefehler NICHT als leer werten: sonst überschriebe der nächste Klick
      // die Serverdaten mit einer leeren Zeile.
      .catch(() => !aus && setFehler(true));
    return () => {
      aus = true;
    };
  }, [firmaId, laden, daten, fehler]);

  const zeile = React.useCallback((id: number) => daten?.get(id) ?? LEER(id), [daten]);
  const speichern = React.useCallback(
    (row: EmployeeCoverageRow): Promise<void> => {
      if (!firmaId) return Promise.resolve();
      setDaten((alt) => new Map(alt ?? []).set(row.personContactId, row));
      const p = saveEmployeeCoverage(firmaId, row);
      p.catch(() => {
        listEmployeeCoverage(firmaId)
          .then((rows) => setDaten(new Map(rows.map((r) => [r.personContactId, r]))))
          .catch(() => setFehler(true));
      });
      return p;
    },
    [firmaId],
  );

  return { aktiv: !!firmaId && daten !== null && !fehler, fehler, zeile, speichern };
}

function AbteilungFeld({ wert, onSpeichern }: { wert: string; onSpeichern: (v: string) => void }) {
  const [text, setText] = React.useState(wert);
  React.useEffect(() => setText(wert), [wert]);
  return (
    <input
      type="text"
      value={text}
      aria-label="Abteilung"
      placeholder="—"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text.trim() !== wert && onSpeichern(text.trim())}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      style={{
        width: "100%",
        minWidth: 0,
        font: "inherit",
        fontSize: "13px",
        border: "1px solid transparent",
        borderRadius: 6,
        padding: "3px 6px",
        background: "transparent",
      }}
      onFocus={(e) => (e.currentTarget.style.borderColor = "#ddd3dc")}
    />
  );
}

const inlineStil: React.CSSProperties = {
  width: "100%",
  minWidth: 0,
  font: "inherit",
  fontSize: "13px",
  border: "1px solid #ddd3dc",
  borderRadius: 6,
  padding: "3px 6px",
  background: "#fff",
};

/**
 * Geschäftliches Telefon der Person zu DIESER Firma, inline (User 30.09.2026):
 * Format „+49 221 12345-67" wie überall (useTelefonFeld, Durchwahl erlaubt,
 * Zentrale der Firma als Muster). Gespeichert wird beim Verlassen — nur, wenn
 * sich die Nummer geändert hat und gültig ist.
 */
export function GeschaeftlichesTelefonFeld({
  wert,
  zentrale,
  onSpeichern,
  label = "Geschäftliches Telefon",
}: {
  wert: string;
  zentrale?: readonly (string | null | undefined)[];
  onSpeichern: (v: string) => void;
  label?: string;
}) {
  const [fehler, setFehler] = React.useState<string | undefined>();
  const feld = useTelefonFeld({
    value: wert,
    geschaeftlich: true,
    zentrale,
    onBlur: (neu) => {
      const f = pruefeTelefon(neu);
      setFehler(f);
      if (f || !neu.trim() || neu === wert) return;
      onSpeichern(neu);
    },
  });
  return (
    <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <input
        {...feld.inputProps}
        aria-label={label}
        aria-invalid={fehler ? true : undefined}
        placeholder="+49 …"
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        style={{ ...inlineStil, ...(fehler ? { borderColor: "#b3261e" } : null) }}
      />
      {fehler && (
        <span role="alert" style={{ fontSize: "11.5px", color: "#b3261e" }}>
          {fehler}
        </span>
      )}
    </span>
  );
}

const MAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Geschäftliche E-Mail der Person zu DIESER Firma, inline — speichert beim Verlassen. */
export function GeschaeftlicheMailFeld({
  wert,
  onSpeichern,
  label = "Geschäftliche E-Mail",
}: {
  wert: string;
  onSpeichern: (v: string) => void;
  label?: string;
}) {
  const [text, setText] = React.useState(wert);
  const [fehler, setFehler] = React.useState<string | undefined>();
  React.useEffect(() => setText(wert), [wert]);
  return (
    <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <input
        type="email"
        value={text}
        aria-label={label}
        aria-invalid={fehler ? true : undefined}
        placeholder="name@firma.de"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const neu = text.trim();
          const f = neu && !MAIL_OK.test(neu) ? "Bitte eine gültige E-Mail-Adresse eingeben." : undefined;
          setFehler(f);
          if (f || !neu || neu === wert.trim()) return;
          onSpeichern(neu);
        }}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        style={{ ...inlineStil, ...(fehler ? { borderColor: "#b3261e" } : null) }}
      />
      {fehler && (
        <span role="alert" style={{ fontSize: "11.5px", color: "#b3261e" }}>
          {fehler}
        </span>
      )}
    </span>
  );
}

/**
 * Inline-Bearbeitung einer Zeile (Stift, User 30.09.2026): Telefon und E-Mail
 * werden zu Eingabefeldern und beim Verlassen sofort gespeichert.
 */
export type InlineBearbeitung = {
  /** `MenschEntry.id` der Zeile, die gerade bearbeitet wird. */
  aktivId: string | null;
  zentrale?: readonly (string | null | undefined)[];
  speichern: (eintrag: MenschEntry, typ: FirmenKommunikationTyp, wert: string) => void;
};

const ABSICHERUNGEN: Array<{ feld: AbsicherungsFeld; titel: React.ReactNode; label: string }> = [
  { feld: "bav", titel: <span title="betriebliche Altersversorgung">bAV</span>, label: "bAV" },
  { feld: "bkv", titel: <span title="betriebliche Krankenversicherung">bKV</span>, label: "bKV" },
  { feld: "uv", titel: <span title="Unfallversicherung">UV</span>, label: "UV" },
  { feld: "betriebshaftpflicht", titel: <span title="Betriebshaftpflicht">BHV</span>, label: "Betriebshaftpflicht" },
];

/**
 * Die Zusatzspalten. Die Zeilen der Liste tragen den Schlüssel
 * `${eintrag.id}-mensch` — darüber findet jede Zelle ihren Eintrag.
 */
export function menschenZusatzSpalten(
  eintraege: MenschEntry[],
  abs: MitarbeiterAbsicherung,
  inline?: InlineBearbeitung,
): ZusatzSpalte[] {
  const nachKey = new Map(eintraege.map((e) => [`${e.id}-mensch`, e]));
  const eintragVon = (z: BeziehungsZeile) => nachKey.get(String(z.key));
  const personId = (z: BeziehungsZeile) => eintragVon(z)?.ref?.contactId ?? null;
  const leer = <span style={{ color: "#9a8c95" }}>—</span>;

  const spalten: ZusatzSpalte[] = [
    {
      key: "tel",
      titel: "Telefon",
      breite: "minmax(0, 0.9fr)",
      // Altbestand steht teils als „+4915…“ in der DB — Anzeige im neuen Format.
      // Nur das geschäftliche Telefon zu DIESER Firma (contactToRelationItems).
      zelle: (z) => {
        const e = eintragVon(z);
        const tel = e?.ref?.kommunikation?.telefon;
        if (e && inline && inline.aktivId === e.id) {
          return (
            <GeschaeftlichesTelefonFeld
              wert={tel ?? ""}
              zentrale={inline.zentrale}
              onSpeichern={(v) => inline.speichern(e, "phone", v)}
            />
          );
        }
        return tel ? formatiereTelefon(tel, { geschaeftlich: true }) || tel : leer;
      },
    },
    {
      key: "mail",
      titel: "E-Mail",
      breite: "minmax(0, 1.1fr)",
      zelle: (z) => {
        const e = eintragVon(z);
        const mail = e?.ref?.kommunikation?.email;
        if (e && inline && inline.aktivId === e.id) {
          return <GeschaeftlicheMailFeld wert={mail ?? ""} onSpeichern={(v) => inline.speichern(e, "email", v)} />;
        }
        return mail || leer;
      },
    },
  ];
  if (!abs.aktiv) return spalten;

  spalten.push({
    key: "abt",
    titel: "Abteilung",
    breite: "minmax(0, 0.8fr)",
    zelle: (z) => {
      const id = personId(z);
      if (!id) return leer;
      const row = abs.zeile(id);
      return (
        <AbteilungFeld wert={row.department ?? ""} onSpeichern={(v) => void abs.speichern({ ...row, department: v || null })} />
      );
    },
  });
  for (const a of ABSICHERUNGEN) {
    spalten.push({
      key: a.feld,
      titel: a.titel,
      breite: "44px",
      mittig: true,
      zelle: (z) => {
        const id = personId(z);
        if (!id) return leer;
        const row = abs.zeile(id);
        return (
          <input
            type="checkbox"
            aria-label={a.label}
            checked={row[a.feld]}
            onChange={() => void abs.speichern({ ...row, [a.feld]: !row[a.feld] })}
            style={{ accentColor: "#7d2169", width: 16, height: 16, margin: 0 }}
          />
        );
      },
    });
  }
  return spalten;
}
